import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useLazyQuery } from '@apollo/client';
import { useMutation } from '@apollo/client';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { IAbstractRecord } from '@models';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { IBaseDrawerProps } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';
import {
  useCreateBusinessPartner,
  useGetBusinessPartnerLovs,
  useGetPersonDetailsById
} from '@common/slices';
import {
  newBusinessPartnerDrawerSections,
  newBusinessPartnerInitialValues
} from '@common/constants';
import Loader from '@components/loader/Loader';

function toLookups(data: any) {
  const personTypes: Record<string, string> = {
    INDIVIDUAL: 'Individual',
    COMPANY: 'Company'
  };

  const bpTypes: Record<string, string> = {
    AGENCY: 'Agency',
    BRANCH: 'Branch',
    BROKER: 'Broker',
    BROKER_POS: 'Broker POS',
    DELEGATE: 'Delegate',
    EMPLOYEE: 'Employee',
    HEADQUARTER: 'Headquarter',
    SALES_REPRESENTATIVE: 'Sales Representative'
  };
  const businessPartnerTypes: Record<string, string> = {};
  data?.BusinessPartnerType?.enumValues?.forEach((el: any) => {
    businessPartnerTypes[el.name] = el.name
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^\w/, (c: any) => c.toUpperCase());
  });

  const addressTypes: Record<string, string> = {
    WORK: 'Work',
    HOME: 'Home'
  };

  const personTitles: Record<string, string> = {};
  data?.core?.getTitles?.forEach((el: any) => {
    personTitles[el.id] = el.title;
  });

  const relatedCompanies: Record<string, string> = {};
  data?.salesforce?.getRelatedCompanies?.forEach((el: any) => {
    relatedCompanies[el.id] = el.title;
  });

  const activePersons: Record<string, string> = {};
  data?.salesforce?.getActivePersonsWithoutBusinessPartners?.forEach(
    (el: any) => {
      activePersons[el.id] = el.fullName;
    }
  );

  const countries: Record<string, string> = {};
  data?.core?.getCountries?.forEach((el: any) => {
    countries[el.id] = el.title;
  });

  const zones: Record<string, string> = {};
  data?.core?.getZones?.forEach((el: any) => {
    zones[el.id] = el.title;
  });

  const currencies: Record<string, string> = {};
  data?.core?.getCurrencies?.forEach((el: any) => {
    currencies[el.id] = el.title;
  });

  return {
    personTypes,
    bpTypes,
    businessPartnerTypes,
    addressTypes,
    personTitles,
    relatedCompanies,
    activePersons,
    countries,
    zones,
    currencies
  };
}

const NewBusinessPartnerDrawer: React.FC<IBaseDrawerProps> = ({
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [values, setValues] = useState<IAbstractRecord>(
    newBusinessPartnerInitialValues
  );

  const [fetchPersonDetails] = useGetPersonDetailsById.useLazy();

  const { data } = useGetBusinessPartnerLovs.useQuery();

  const [createBusinessPartner] = useCreateBusinessPartner();

  const lovs = useMemo<Record<string, Record<string, string>>>(() => {
    if (!data) return {};

    return toLookups({
      salesforce: {
        getActivePersonsWithoutBusinessPartners:
          data?.salesforce?.getActivePersonsWithoutBusinessPartners,
        getRelatedCompanies: data?.salesforce?.getRelatedCompanies
      },
      core: {
        getTitles: data?.core?.getTitles,
        getCountries: data?.core?.getCountries,
        getZones: data?.core?.getZones,
        getCurrencies: data?.core?.getCurrencies
      },
      BusinessPartnerType: data?.BusinessPartnerType
    });
  }, [data]);

  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    if (!data) return [];

    return newBusinessPartnerDrawerSections(values, lovs);
  }, [values, lovs, data]);

  const handleChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    currentValues: IAbstractRecord,
    errors: Record<string, string>,
    touched: Record<string, boolean>
  ) => {
    let updated = { ...currentValues, [fieldName]: value };

    const isHardSwitch = fieldName === 'type' || fieldName === 'newPerson';

    if (fieldName === 'newPerson') {
      updated = {
        ...newBusinessPartnerInitialValues,
        newPerson: value
      };
    }

    if (fieldName === 'type') {
      updated = {
        ...newBusinessPartnerInitialValues,
        newPerson: currentValues.newPerson,
        type: value
      };
    }

    if (fieldName === 'person' && value) {
      const res = await fetchPersonDetails({ variables: { personId: value } });
      const p = res?.data?.salesforce?.getPersonById;
      if (p) {
        updated.email = p.email || '';
        updated.mobileNumber = p.phoneDetails?.phoneNumber || '';
      }
    }

    if (isHardSwitch) {
      Object.keys(errors).forEach((k) => delete errors[k]);
      Object.keys(touched).forEach((k) => delete touched[k]);
    }

    setValues(updated);
  };

  const onSubmit = async (formData: IAbstractRecord) => {
    const payload = {
      newPerson: formData.newPerson,
      personType: formData.type || null,
      personTitle: formData.title || null,
      firstName: formData.firstName || null,
      middleName: formData.middleName || null,
      lastName: formData.lastName || null,
      dateOfBirth: formData.dateOfBirth || null,
      personId: formData.person || null,
      companyName: formData.companyName || null,
      email: formData.email || null,
      mobileNumber: formData.mobileNumber || null,
      relatedCompany: formData.relatedCompany || null,
      businessPartnerType: formData.bpType || null,
      isCompany: formData.isCompany,
      isLocal: formData.isLocal,
      isRegistered: formData.isRegistered,
      registrationNumber: formData.registerNumber || null,
      registrationExpiryDate: formData.registrationExpiryDate || null,
      addressType: formData.addressType || null,
      taxAuxiliary: formData.taxAuxiliary || null,
      auxiliary: formData.auxiliary || null,
      maxCreditLimitAllowedCurrency:
        formData.maxCreditLimitAllowedCurrency || null,
      maxCreditLimit: Number(formData.maxCreditLimitAllowed || 0),
      allowAccessToAllInsured: formData.allowAccessToAllInsured,
      address: {
        country: formData.country || null,
        zone: formData.zone || null,
        city: formData.city || null,
        street: formData.street || null,
        building: formData.building || null,
        addressDetails: formData.addressDetails || null,
        addressDetailsArabic: formData.addressDetailsArabic || null
      }
    };

    const res = await createBusinessPartner({
      createBusinessPartnerInput: payload
    });
    const createdId = res.data?.salesforce?.createBusinessPartner;
    onSuccess(`/salesforce/business-partners/${createdId}`);
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={onSubmit}
      onChange={handleChange}
      submitButtonState={submitButtonState}
    />
  );
};

export { NewBusinessPartnerDrawer };
